import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import ProductDemoCTA from "./ProductDemoCTA";

const products = [
  {
    id: "school-management",
    title: "School / Learning Management System",
    description:
      "Comprehensive school administration with student records, attendance tracking, grade management, and parent communication.",
    points: [
      "Student Information System",
      "Examination & Result Processing",
      "Parent-Teacher Communication Portal",
    ],
    img: "/products/schoolmag.png",
  },
  {
    id: "hotel-management",
    title: "Hotel Management System",
    description:
      "Complete hotel operations management including room bookings, housekeeping, and restaurant POS integration.",
    points: [
      "Room Booking & Reservations",
      "POS Integration for Restaurants",
      "Night Audit & Financial Reporting",
    ],
    img: "/products/hotelm.jpg",
  },
  {
    id: "clinic-management",
    title: "Clinic Management System",
    description:
      "Healthcare management solution for clinics with patient records, appointments, and pharmacy integration.",
    points: [
      "Patient Records & Appointments",
      "Pharmacy & Inventory Control",
      "Insurance Claim Integration",
    ],
    img: "/products/clientmg.png",
  },
  {
    id: "erp-crm",
    title: "ERP & CRM Solutions",
    description:
      "Enterprise resource planning and customer relationship management for business optimization.",
    points: [
      "Sales & Inventory Management",
      "HR & Payroll Automation",
      "Dynamic Dashboard Analytics",
    ],
    img: "/products/crmerp.jpg",
  },
  {
    id: "cloud-pbx",
    title: "Cloud PBX",
    description:
      "Cloud-based phone system for businesses with advanced call routing and remote team accessibility.",
    points: [
      "Call Recording & Analytics",
      "Remote Team Accessibility",
      "Hardware-free Setup",
    ],
    img: "/products/cloudpbx.png",
  },
  {
    id: "sms-gateway",
    title: "SMS Gateway",
    description:
      "Bulk SMS messaging platform for marketing and transactional communications with delivery tracking.",
    points: [
      "Bulk SMS Campaigns",
      "Message Queuing & Delivery Reports",
      "Two-way Messaging & Security",
    ],
    img: "/products/smsgate.png",
  },
  {
    id: "payment-gateway",
    title: "Payment Gateway",
    description:
      "Secure payment processing solution supporting multiple payment methods and currencies.",
    points: [
      "Multi-currency Support",
      "International Card Support (Visa/JCB)",
      "Encrypted Data Compliance",
    ],
    img: "/products/paymentgate.png",
  },
  {
    id: "marketing-tools",
    title: "Marketing Tools",
    description:
      "Comprehensive marketing automation platform for customer engagement and campaign management.",
    points: [
      "Email Campaign Management",
      "Multi-channel (SMS & Email)",
      "Audience Engagement Analytics",
    ],
    img: "/products/marketingtools.png",
  },
  {
    id: "top-up-gateway",
    title: "Topup Gateway",
    description:
      "Reliable digital topup gateway solutions for airtime recharge, game credits, wallet balance, and automated payment processing.",
    points: [
      "Mobile Topup API",
      "Instant Recharge System",
      "Multi-provider Integration",
      "Secure Transactions",
    ],
    img: "/services/topupgate.jpg",
  },
];

export default function ProductPage() {
  return (
    <div className="min-h-screen bg-[#0B0F19] text-white font-sans pb-20 md:pb-32">
      {/* Hero Section */}
      <section className="relative min-h-[400px] md:h-[600px] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src="/photos/hero/bg4.jpg"
            className="w-full h-full object-cover opacity-30 scale-105"
            alt="Background"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0B0F19] via-transparent to-[#0B0F19]"></div>
        </div>

        <div className="relative z-10 text-center px-4 md:px-6 max-w-5xl">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[400px] h-[400px] md:w-[600px] md:h-[600px] bg-cyan-500/5 rounded-full blur-[80px] md:blur-[120px] pointer-events-none -z-10"></div>

          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-3xl sm:text-4xl md:text-5xl lg:text-7xl font-black uppercase italic tracking-tighter leading-[0.9] md:leading-[0.85] mb-4 md:mb-6">
            Our{" "}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-500">
              Products
            </span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
            className="max-w-2xl mx-auto text-gray-400 text-sm md:text-lg lg:text-xl font-medium leading-relaxed px-2">
            Comprehensive products to transform your business
          </motion.p>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 md:px-6 py-12 md:py-16">
        {/* Products Section */}
        <section className="mb-12 md:mb-20">
          <h2 className="text-2xl md:text-3xl font-black uppercase italic mb-6 md:mb-8 text-[#00BCF0]">
            Products
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
            {products.map((product, index) => (
              <motion.div
                key={product.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="group relative bg-[#161B22] border border-white/10 rounded-xl md:rounded-2xl overflow-hidden hover:border-[#00BCF0]/40 transition-all">
                <div className="aspect-video overflow-hidden">
                  <img
                    src={product.img}
                    alt={product.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-4 md:p-6">
                  <h3 className="text-lg md:text-xl font-black text-white mb-2">
                    {product.title}
                  </h3>
                  <p className="text-xs md:text-sm text-slate-400 mb-4 line-clamp-2">
                    {product.description}
                  </p>
                  <Link
                    to={`/products/${product.id}`}
                    className="inline-flex items-center gap-2 text-[#00BCF0] font-semibold hover:text-white transition-colors text-sm md:text-base">
                    Learn More
                    <ArrowRight
                      size={16}
                      className="group-hover:translate-x-1 transition-transform"
                    />
                  </Link>
                </div>
              </motion.div>
            ))}
          </div>
        </section>
      </div>

      <ProductDemoCTA />
    </div>
  );
}
